/**
 * Wallet connection for this app, on Stacks Connect v8's SIP-030 API.
 *
 * Replaces the per-file `new AppConfig(...)` / `new UserSession(...)` pairs and `showConnect`.
 * Those still exist in v8, but only as `@deprecated` shims.
 *
 * Two deliberate compatibility decisions:
 *
 * 1. **The legacy `userData` shape is preserved.** ~108 places in `src/` read
 *    `userData.profile.stxAddress.{mainnet|testnet}`, and 29 of them read it straight out of
 *    `localStorage['userData']`. `connectWallet()` therefore writes that exact shape, derived the
 *    same way the deprecated shim derived it (see `toLegacyUserData`). Nothing downstream changes.
 *
 * 2. **`localStorage['userData']` remains the app's source of truth for "signed in".** v8 keeps
 *    its own record under `@stacks/connect`, but a user who connected under the old code has only
 *    the `userData` key. Keying off `userData` means existing sessions survive this upgrade
 *    instead of everyone being silently signed out.
 */
import { connect, disconnect, DEFAULT_PROVIDERS } from '@stacks/connect';
import type { MethodResult } from '@stacks/connect';

/**
 * `AddressEntry` is not re-exported from the package index, so derive it from the public
 * `getAddresses` result type rather than importing a deep internal path.
 */
type AddressEntry = MethodResult<'getAddresses'>['addresses'][number];

/** Leather (formerly Hiro Wallet). */
export const LEATHER_PROVIDER_ID = 'LeatherProvider';
/** Xverse. Note the id is namespaced under its Bitcoin provider. */
export const XVERSE_PROVIDER_ID = 'XverseProviders.BitcoinProvider';

/**
 * The only wallets this app offers.
 *
 * v8 ships four providers by default — Leather, Xverse, Asigna Multisig and Fordefi — and would
 * additionally surface any other installed provider. `approvedProviderIds` restricts both.
 */
export const APPROVED_PROVIDER_IDS: string[] = [LEATHER_PROVIDER_ID, XVERSE_PROVIDER_ID];

/**
 * Pass this to every `connect()` and `request()` call.
 *
 * `approvedProviderIds` filters what the modal will show. `defaultProviders` is also filtered so
 * the unsupported wallets are not offered as "install this" suggestions when absent.
 *
 * This is a *per-request* option in v8 — there is no global configuration — so omitting it
 * anywhere would let the full wallet list reappear at that call site.
 */
export const WALLET_SELECTION_OPTIONS = {
  approvedProviderIds: APPROVED_PROVIDER_IDS,
  defaultProviders: DEFAULT_PROVIDERS.filter((provider) =>
    APPROVED_PROVIDER_IDS.includes(provider.id),
  ),
};

export type LegacyStxAddress = { mainnet: string; testnet: string };
export type LegacyUserData = { profile: { stxAddress: LegacyStxAddress } };

const USER_DATA_KEY = 'userData';

/**
 * Rebuild the legacy `userData` object from a SIP-030 `getAddresses` result.
 *
 * Mirrors what `@stacks/connect@8`'s deprecated `showConnect` shim does, including the details
 * that are easy to get wrong:
 *  - the STX entry is the one with `symbol === 'STX'`, else the first address starting with `S`
 *  - the address is upper-cased
 *  - mainnet is decided by the second character (`SP…`/`SM…`), not by app config
 *  - **only the matching key is populated; the other stays `''`** rather than holding a
 *    converted address
 */
export function toLegacyUserData(addresses: AddressEntry[]): LegacyUserData {
  const stxAddress: LegacyStxAddress = { mainnet: '', testnet: '' };

  const entry = addresses?.find(
    (candidate) => candidate?.symbol === 'STX' || candidate?.address?.startsWith('S'),
  );

  if (entry?.address) {
    const address = entry.address.toUpperCase();
    const isMainnetAddress = address[1] === 'P' || address[1] === 'M';
    stxAddress[isMainnetAddress ? 'mainnet' : 'testnet'] = address;
  }

  return { profile: { stxAddress } };
}

/** Read the stored session, or `null` when absent/corrupt. */
export function getWalletUserData(): LegacyUserData | null {
  try {
    const raw = localStorage.getItem(USER_DATA_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.profile?.stxAddress ? parsed : null;
  } catch {
    return null;
  }
}

/** The connected STX address, whichever network it belongs to. `undefined` when signed out. */
export function getStxAddress(): string | undefined {
  const stxAddress = getWalletUserData()?.profile?.stxAddress;
  return stxAddress?.mainnet || stxAddress?.testnet || undefined;
}

/** Replaces `userSession.isUserSignedIn()`. */
export function isWalletConnected(): boolean {
  return getStxAddress() !== undefined;
}

/**
 * Open the wallet picker (Leather / Xverse only) and persist the session.
 *
 * Replaces `showConnect({ appDetails, redirectTo, onFinish, userSession })`. `appDetails` has no
 * SIP-030 equivalent — the wallet supplies app identity now — and `redirectTo` was unused because
 * every call site reloaded the page itself.
 *
 * Rejects if the user dismisses the modal, so callers should catch.
 */
export async function connectWallet(): Promise<LegacyUserData> {
  const result = await connect({ ...WALLET_SELECTION_OPTIONS, forceWalletSelect: true });
  const userData = toLegacyUserData(result.addresses);
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  return userData;
}

/**
 * Replaces `userSession.signUserOut()` + `localStorage.clear()`.
 *
 * `disconnect()` clears v8's own storage and the remembered provider choice; `localStorage.clear()`
 * matches what the existing logout did, so no other app state survives a sign-out.
 */
export function disconnectWallet(): void {
  try {
    disconnect();
  } finally {
    localStorage.clear();
  }
}
